import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { NotificationPriority } from '../../../types/user';
import { RecipientFilter } from '../../../types/notification-admin';
import { UserRole } from '../../../types/auth';

interface BroadcastPreviewProps {
  title: string;
  message: string;
  priority: NotificationPriority;
  actionLabel?: string;
  actionUrl?: string;
  recipientFilter: RecipientFilter;
  recipientCount: number;
  scheduledFor?: Date;
}

/**
 * BroadcastPreview - Preview component showing how the broadcast will appear
 * and summarizing all configuration details
 */
const BroadcastPreview: React.FC<BroadcastPreviewProps> = ({
  title,
  message,
  priority,
  actionLabel,
  actionUrl,
  recipientFilter,
  recipientCount,
  scheduledFor,
}) => {
  const getPriorityColor = (p: NotificationPriority) => {
    switch (p) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRecipientDescription = (): string => {
    if (recipientFilter.allUsers) {
      return 'All users in the system';
    }

    if (recipientFilter.customUserIds && recipientFilter.customUserIds.length > 0) {
      return `${recipientFilter.customUserIds.length} custom selected users`;
    }

    const parts: string[] = [];

    if (recipientFilter.roles && recipientFilter.roles.length > 0) {
      parts.push(`Roles: ${recipientFilter.roles.join(', ')}`);
    }

    if (recipientFilter.activityStatus) {
      parts.push(`Activity: ${recipientFilter.activityStatus}`);
    }

    if (recipientFilter.registrationDateRange) {
      parts.push(
        `Registered: ${format(recipientFilter.registrationDateRange.start, 'PP')} - ${format(
          recipientFilter.registrationDateRange.end,
          'PP'
        )}`
      );
    }

    return parts.length > 0 ? parts.join(', ') : 'No filters applied';
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Preview & Confirm
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Recipients
                </Typography>
              </Box>
              <Typography variant="h4">{recipientCount}</Typography>
              <Typography variant="caption" color="text.secondary">
                users will receive this
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <NotificationsIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Priority
                </Typography>
              </Box>
              <Chip
                label={priority.toUpperCase()}
                color={getPriorityColor(priority) as any}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Notification priority level
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {scheduledFor ? <ScheduleIcon color="primary" /> : <SendIcon color="primary" />}
                <Typography variant="subtitle2" color="text.secondary">
                  Delivery
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold">
                {scheduledFor ? 'Scheduled' : 'Immediate'}
              </Typography>
              {scheduledFor && (
                <Typography variant="caption" color="text.secondary">
                  {format(scheduledFor, 'PPpp')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notification Preview */}
      <Paper sx={{ p: 0, mb: 3 }} variant="outlined">
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2 }}>
          <Typography variant="subtitle2">Notification Preview</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Card
            variant="outlined"
            sx={{
              maxWidth: 400,
              mx: 'auto',
              borderLeft: `4px solid`,
              borderLeftColor: `${getPriorityColor(priority)}.main`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <NotificationsIcon fontSize="small" color={getPriorityColor(priority) as any} />
                <Chip label={priority.toUpperCase()} color={getPriorityColor(priority) as any} size="small" />
              </Box>

              <Typography variant="h6" gutterBottom>
                {title || '(No title)'}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                {message || '(No message)'}
              </Typography>

              {actionLabel && actionUrl && (
                <Button variant="contained" size="small" fullWidth sx={{ mt: 1 }}>
                  {actionLabel}
                </Button>
              )}

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Just now • Admin Broadcast
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      {/* Recipient Details */}
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Recipient Filters
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" paragraph>
          {getRecipientDescription()}
        </Typography>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>{recipientCount}</strong> user{recipientCount !== 1 ? 's' : ''} match the current filter criteria.
          </Typography>
        </Alert>
      </Paper>

      {/* Warning for immediate send */}
      {!scheduledFor && recipientCount > 10 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            You are about to send a broadcast to <strong>{recipientCount} users</strong> immediately.
            This action cannot be undone. Please review the message carefully before sending.
          </Typography>
        </Alert>
      )}

      {/* Warning for scheduled send */}
      {scheduledFor && (
        <Alert severity="info">
          <Typography variant="body2">
            This broadcast is scheduled to be sent on{' '}
            <strong>{format(scheduledFor, 'PPpp')}</strong>.
            You can cancel or modify it before the scheduled time.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default BroadcastPreview;
